import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  RecurringCutoffSummary,
  RecurringCutoffActionResult,
  RecurringDonorDto,
  RecurringDonorCancelResponse,
  PullInCutoffRequest,
} from '@signalvote/shared';
import { authService } from '../../services/authService';
import { apiJson } from './apiFetch';
import type { ApiErrorSpec } from './apiFetch';
import { queryKeys } from '../../lib/queryKeys';
import { useCampaignStore } from '../../stores';
import { FINANCE_STALE_TIME } from './queryUtils';

// ADR-069 (#616) — the platform-admin / finance-manager Recurring Cutoff surface. A read summary plus
// two campaign-wide wind-down mutations (pull the cutoff in / stop all recurring now). These are bulk
// sweeps over the whole committee, not single-cached-row edits, so there is no per-row concurrency
// token to thread — each mutation simply invalidates the summary so the counts/cutoff reconcile.

export type { RecurringCutoffSummary, RecurringCutoffActionResult, RecurringDonorDto };

/**
 * A rejected recurring-cutoff request (e.g. a non-future date → 400 CutoffNotInFuture). Carries the
 * server error code so the form can surface a precise message.
 */
export class RecurringCutoffError extends Error {
  code: string;
  status: number;
  constructor(code: string, status: number, message: string) {
    super(message);
    this.name = 'RecurringCutoffError';
    this.code = code;
    this.status = status;
  }
}

/** GET /api/finance/recurring — availability + effective cutoff + active-series count. */
export function useRecurringCutoffSummary() {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.recurring(),
    queryFn: () =>
      apiJson<RecurringCutoffSummary>('/api/finance/recurring', {
        error: 'Failed to fetch recurring cutoff summary',
      }),
    enabled: authService.isAuthenticated() && !!selectedCampaign,
    staleTime: FINANCE_STALE_TIME,
  });
}

const actionError: ApiErrorSpec = (body, response) =>
  new RecurringCutoffError(
    body.code ?? '',
    response.status,
    body.error ?? 'Recurring cutoff action failed',
  );

/**
 * POST /api/finance/recurring/pull-in-cutoff — set an earlier cancel_at on every active subscription
 * bounded later than `cutoff` (an ISO `yyyy-MM-dd` date). Invalidates the summary on success.
 */
export function usePullInRecurringCutoff() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cutoff: string) =>
      apiJson<RecurringCutoffActionResult>('/api/finance/recurring/pull-in-cutoff', {
        method: 'POST',
        json: { cutoff } satisfies PullInCutoffRequest,
        error: actionError,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.recurring() });
    },
  });
}

/**
 * POST /api/finance/recurring/stop-all — cancel every active subscription for the campaign now; each
 * mirror moves to Canceled. Invalidates the summary on success.
 */
export function useStopAllRecurring() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      apiJson<RecurringCutoffActionResult>('/api/finance/recurring/stop-all', {
        method: 'POST',
        error: actionError,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.recurring() });
    },
  });
}

// The per-donor recurring-donors list on the Donations page — who is giving recurringly (donor / amount /
// cadence / started / next-charge / status). A pure DB read; Finance/View. Distinct from the platform-admin
// wind-down summary above.

/** GET /api/finance/recurring/donors — the campaign's recurring donors, Active first. */
export function useRecurringDonors() {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.recurringDonors(),
    queryFn: () =>
      apiJson<RecurringDonorDto[]>('/api/finance/recurring/donors', {
        error: 'Failed to fetch recurring donors',
      }),
    enabled: authService.isAuthenticated() && !!selectedCampaign,
    staleTime: FINANCE_STALE_TIME,
  });
}

/**
 * POST /api/finance/recurring/donors/{id}/cancel — cancel ONE donor's recurring series (Finance/Manage).
 * Invalidates the donors list on success. The status guard on the server makes a racing double-cancel a
 * clean no-op, so there is no per-row concurrency token to thread here.
 */
export function useCancelRecurringDonor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (recurringDonationId: string) =>
      apiJson<RecurringDonorCancelResponse>(
        `/api/finance/recurring/donors/${recurringDonationId}/cancel`,
        { method: 'POST', error: 'Failed to cancel the recurring donation' },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.recurringDonors() });
    },
  });
}
